import { DEVICE_REQUESTS_ROLES, LOGISTIC_BOX_ROLES, LOGISTIC_BOX_ALLOWED_EMAILS, LOGISTIC_BOX_OPEN_SLUGS, PrivateRoutes } from '@/constants/routes'
import { IConfigSeller } from '@/models/configSeller'
import { FC, useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams, useLocation } from 'react-router-dom'

import IconHome from '../../icons/IconHome.svg'
import IconQuote from '../../icons/IconQuote.svg'
import IconForm from '../../icons/IconForm.svg'
import IconDashboard from '../../icons/IconDashboard.svg'
import IconLogisticBox from '../../icons/IconLogisticBox.svg'
import IconDeviceRequest from '../../icons/IconDeviceRequest.svg'
import IconHelp from '../../icons/IconHelp.svg'
import './SidebarNew.css'

interface SidebarItem {
  to?: string
  href?: string
  icon: string
  label: string
}

const rolesDashboard = (import.meta.env.VITE_COUNTRY === 'MX') ? [17, 19, 27] : (import.meta.env.VITE_COUNTRY === 'CL') ? [1, 33, 40] : (import.meta.env.VITE_COUNTRY === 'PE') ? [1, 13, 18] : []
// Mismos roles con los que se protege la ruta LOADSAMSUNGDATA en App.tsx
const rolesCargaMasiva = [Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER)]

const SidebarNew: FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const [expanded, setExpanded] = useState(false)
  const [closing, setClosing] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const userState = useSelector((store: { user: { id_role: number, email: string } }) => store.user)
  // Acceso: en slugs abiertos (Falabella) lo ve cualquier usuario; en el resto,
  // rol permitido (admin/jefe) Y correo en la lista de liberación gradual.
  const isLogisticBoxOpenSlug = LOGISTIC_BOX_OPEN_SLUGS
    .map(s => s.toLowerCase())
    .includes(String(slug ?? '').toLowerCase())
  const canSeeLogisticBox = isLogisticBoxOpenSlug || (
    LOGISTIC_BOX_ROLES.includes(userState.id_role) &&
    LOGISTIC_BOX_ALLOWED_EMAILS.map(e => e.toLowerCase()).includes(String(userState.email ?? '').toLowerCase().trim())
  )
  // Dispositivos que se pidió incorporar al cotizador: solo admin y jefe de tienda.
  const canSeeDeviceRequests = DEVICE_REQUESTS_ROLES.includes(userState.id_role)
  const isSamsungPeru = configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isSamsungRomPeru = configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe_rom'
  const isTelcelCorpMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_corp_mx'
  const cotizarSellers = [
    'ti_samsung_pe_rom', 'ti_falabella', 'ti_falabella_pe', 'ti_motorola',
    'ti_coppel', 'ti_wom', 'ti_oppo', 'ti_elektra_mx', 'ti_xiaomi', 'ti_conecta_peru',
    'ti_claro', 'ti_samsung_mx', 'ti_phantom', 'ti_ripley_pe', 'ti_paris',
    'ti_ripley_cl', 'ti_huawei_mx', 'ti_huawei_pe', 'ti_huawei_cl', 'ti_telcel_mx', 'ti_telcel_corp_mx',
    'ti_huawei_online_cl', 'ti_huawei_online_mx', 'ti_huawei_online_pe', 'ti_mercado_libre_cl', 'ti_tecnobuy_cl', 'ti_liverpool_mx'
  ]

  const handleOpen = () => {
    setClosing(false)
    setExpanded(true)
  }

  const handleClose = () => {
    setClosing(true)
    setTimeout(() => {
      setExpanded(false)
      setClosing(false)
    }, 1600)
  }

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (expanded && sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        handleClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [expanded])

  const isActive = (path: string) => location.pathname === path

  const buildItems = (): SidebarItem[] => {
    const items: SidebarItem[] = []

    // HOME
    items.push({
      to: PrivateRoutes.HOME.replace(':slug', String(slug)),
      icon: IconHome,
      label: 'Home'
    })

    // COTIZAR
    const isMacstore = configSeller.config.s3.FOLDER_APP === 'ti_macstore'
    const showCotizar = isMacstore || cotizarSellers.includes(configSeller.config.s3.FOLDER_APP)
    if (showCotizar) {
      items.push({
        to: PrivateRoutes.FORMULARIO.replace(':slug', String(slug)),
        icon: IconQuote,
        label: 'Cotizar'
      })
    }

    // FORMULARIO PICK UP
    if (!isSamsungPeru && !isHuaweiOnlineChile && !isHuaweiOnlineMexico && !isHuaweiOnlinePeru) {
      items.push({
        to: PrivateRoutes.PICKUP_FORM.replace(':slug', String(slug)),
        icon: IconForm,
        label: 'Formulario Pick Up'
      })
    }

    if (isSamsungPeru || isHuaweiOnlineChile || isHuaweiOnlineMexico || isHuaweiOnlinePeru || isSamsungRomPeru) {
      items.push({
        to: PrivateRoutes.DATABANK.replace(':slug', String(slug)),
        icon: IconForm,
        label: 'Datos Bancarios'
      })
    }

    // CARGA MASIVA DE COTIZACIONES (solo telcel corporativo, perfiles admin)
    if (isTelcelCorpMexico && rolesCargaMasiva.includes(userState.id_role)) {
      items.push({
        to: PrivateRoutes.LOADTELCELDATA.replace(':slug', String(slug)),
        icon: IconForm,
        label: 'Carga masiva'
      })
    }

    // DASHBOARD (solo roles 17, 19, 27)
    if (rolesDashboard.includes(userState.id_role)) {
      items.push({
        to: PrivateRoutes.DASHBOARD.replace(':slug', String(slug)),
        icon: IconDashboard,
        label: 'Dashboard'
      })
    }

    // DISPOSITIVOS SOLICITADOS (admin y jefe de tienda)
    if (canSeeDeviceRequests) {
      items.push({
        to: PrivateRoutes.DEVICE_REQUESTS.replace(':slug', String(slug)),
        icon: IconDeviceRequest,
        label: 'Dispositivos solicitados'
      })
    }

    // LOGISTIC BOX (solo correos autorizados)
    if (canSeeLogisticBox) {
      items.push({
        to: PrivateRoutes.LOGISTIC_BOX.replace(':slug', String(slug)),
        icon: IconLogisticBox,
        label: 'Logística de cajas'
      })
    }

    // CENTRO DE AYUDA
    items.push({
      href: configSeller.config.linkCA,
      icon: IconHelp,
      label: 'Centro de ayuda'
    })

    return items
  }

  const items = buildItems()

  const renderItem = (item: SidebarItem, index: number) => {
    const active = item.to ? isActive(item.to) : false
    const content = (
      <>
        <div className='sidebar-icon-wrapper'>
          <img src={item.icon} alt={item.label} className='sidebar-icon' />
        </div>
        {expanded && !closing && (
          <span className='sidebar-label' style={{ animationDelay: `${index * 30}ms` }}>
            {item.label}
          </span>
        )}
        {closing && expanded && (
          <span className='sidebar-label sidebar-label-exit' style={{ animationDelay: `${(items.length - index) * 20}ms` }}>
            {item.label}
          </span>
        )}
      </>
    )

    const className = `sidebar-item ${active ? 'sidebar-item-active' : ''}`

    if (!expanded) {
      return (
        <div key={index} className={className} onClick={handleOpen}>
          {content}
        </div>
      )
    }

    if (item.to) {
      return (
        <Link key={index} to={item.to} className={className}>
          {content}
        </Link>
      )
    }
    if (item.href) {
      return (
        <a key={index} href={item.href} target='_blank' rel='noreferrer' className={className}>
          {content}
        </a>
      )
    }
    return (
      <div key={index} className={`${className} sidebar-item-disabled`}>
        {content}
      </div>
    )
  }

  return (
    <div ref={sidebarRef} className='sidebar-wrapper'>
      <div className='sidebar-back-placeholder'>
        {expanded && (
          <button className={`sidebar-back ${closing ? 'sidebar-back-exit' : ''}`} onClick={handleClose}>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
              <circle cx='12' cy='12' r='11' stroke='#44526B' strokeWidth='2' />
              <path d='M14 8L10 12L14 16' stroke='#44526B' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        )}
      </div>
      <div className={`sidebar-new ${expanded ? 'sidebar-expanded' : 'sidebar-collapsed'} ${closing ? 'sidebar-closing' : ''}`}>
        {/* Back arrow when expanded */}

        {/* Nav items */}
        <nav className='sidebar-nav'>
          {items.map((item, i) => renderItem(item, i))}
        </nav>
      </div>
    </div>
  )
}

export default SidebarNew
